import React from 'react'
import { formatPrice, formatPriceFrom } from '../../utils/currencyUtils'
import { useCurrency } from '../../contexts/CurrencyContext'
import { getBookingProfessionalDisplay } from './dashboardUtils'
import { optimizeProfessionalImage } from '../../utils/supabaseImageUtils'

const FavoritesTab = ({
  favoriteTab,
  setFavoriteTab,
  favoriteServices,
  favoriteSpecialists,
  favoriteServicesWithAssociations,
  favoriteProfessionalsWithAssociations,
  favoriteAppointments,
  categories,
  currency,
  toggleFavorite,
  onBookNow,
  setSelectedFavoriteSpecialist,
  setShowFavoriteModal,
  navigate,
  onCloseSidebar,
  setBookingData,
  setShowServiceBooking,
  setServiceBookingStep,
  onRemoveFavoriteAppointment
}) => {
  // Some appointment payloads may contain raw numeric prices; always format with user's currency.
  const { currency: selectedCurrency } = useCurrency()
  const parseDurationMinutes = (duration, fallback = 60) => {
    if (typeof duration === 'number' && Number.isFinite(duration)) return duration
    if (typeof duration === 'string') {
      const match = duration.match(/\d+/)
      if (match) {
        const minutes = parseInt(match[0], 10)
        return Number.isFinite(minutes) ? minutes : fallback
      }
    }
    return fallback
  }

  const parsePriceNumber = (value, fallback = 0) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string') {
      // Extract first numeric value, supports formats like "from $ 12.00" or "Rs. 1,234"
      const cleaned = value.replace(/,/g, '')
      const match = cleaned.match(/-?\d+(\.\d+)?/)
      if (match) {
        const num = parseFloat(match[0])
        return Number.isFinite(num) ? num : fallback
      }
    }
    return fallback
  }

  // Associations are the source of truth (includes deactivated items from DB).
  // Fall back to catalog-derived lists when associations are not loaded yet.
  const displayFavoriteServices =
    favoriteServicesWithAssociations?.length > 0
      ? favoriteServicesWithAssociations
      : favoriteServices || []

  const displayFavoriteSpecialists =
    favoriteProfessionalsWithAssociations?.length > 0
      ? favoriteProfessionalsWithAssociations
      : favoriteSpecialists || []

  const DeactivatedTag = ({ label = 'Deactivated' }) => (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-gray-100 text-gray-600 border border-gray-200">
      {label}
    </span>
  )

  return (
    <div className="p-4 sm:p-6">
      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 bg-gray-100/50 rounded-xl p-1.5">
        <button
          onClick={() => setFavoriteTab('services')}
          className={`flex-1 px-4 py-2.5 font-semibold text-sm rounded-lg transition-all duration-200 ${
            favoriteTab === 'services'
              ? 'bg-white text-primary shadow-md'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <i className={`fas ${favoriteTab === 'services' ? 'fa-spa' : 'fa-spa'} mr-2`}></i>
          Services
        </button>
        <button
          onClick={() => setFavoriteTab('staff')}
          className={`flex-1 px-4 py-2.5 font-semibold text-sm rounded-lg transition-all duration-200 ${
            favoriteTab === 'staff'
              ? 'bg-white text-primary shadow-md'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <i className={`fas ${favoriteTab === 'staff' ? 'fa-users' : 'fa-users'} mr-2`}></i>
          Professionals
        </button>
        <button
          onClick={() => setFavoriteTab('appointments')}
          className={`flex-1 px-4 py-2.5 font-semibold text-sm rounded-lg transition-all duration-200 ${
            favoriteTab === 'appointments'
              ? 'bg-white text-primary shadow-md'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <i className={`fas ${favoriteTab === 'appointments' ? 'fa-calendar-check' : 'fa-calendar-check'} mr-2`}></i>
          Appointments
        </button>
      </div>

      {favoriteTab === 'services' ? (
        displayFavoriteServices.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 mb-4">
              <i className="far fa-heart text-3xl text-purple-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No favorite services yet</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">Tap the heart icon on any service to save it here for quick access.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {displayFavoriteServices.map((service) => {
              // Get associated professional if available
              const associatedProfessional = service.associatedProfessional || null
              // Find the category for this service
              const serviceCategory = categories.find(cat => 
                cat.services?.some(s => s.id === service.id)
              )
              
              // Normalize service data to handle different property names
              const getServiceDuration = () => {
                if (service.duration) {
                  // Already formatted like "50 min"
                  return service.duration
                }
                if (service.duration_minutes) {
                  // Number of minutes
                  return `${service.duration_minutes} min`
                }
                return 'N/A'
              }
              
              const getServicePrice = () => {
                if (service.price !== undefined && service.price !== null) {
                  return parseFloat(service.price)
                }
                if (service.price_from !== undefined && service.price_from !== null) {
                  return parseFloat(service.price_from)
                }
                if (service.base_price !== undefined && service.base_price !== null) {
                  return parseFloat(service.base_price)
                }
                return 0
              }
              
              const serviceDeactivated =
                service.isDeactivated === true || service.is_active === false
              const proDeactivated =
                associatedProfessional?.isDeactivated === true ||
                associatedProfessional?.is_active === false ||
                service.associatedProfessionalDeactivated === true
              const bookingBlocked = serviceDeactivated || proDeactivated

              return (
                <div key={service.id} className="group bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-lg hover:border-purple-200 transition-all duration-200 overflow-hidden">
                  <div className="p-5">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 pr-3">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-bold text-lg text-gray-900">{service.name}</h3>
                          {serviceDeactivated && <DeactivatedTag />}
                          <button
                            onClick={() => toggleFavorite('services', service.id)}
                            className="w-7 h-7 rounded-full bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 active:scale-95 transition-all duration-200 opacity-100 md:opacity-0 md:group-hover:opacity-100"
                            aria-label="Remove favorite service"
                          >
                            <i className="fas fa-heart text-xs"></i>
                          </button>
                        </div>
                        {serviceCategory && (
                          <p className="text-xs text-purple-600 font-medium mb-2">{serviceCategory.name}</p>
                        )}
                        {/* Show associated professional if available */}
                        {associatedProfessional && (
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <div className="flex items-center gap-2 bg-purple-50 px-2 py-1 rounded-lg">
                              {associatedProfessional.image || associatedProfessional.image_url ? (
                                <img
                                  src={associatedProfessional.image || associatedProfessional.image_url}
                                  alt={associatedProfessional.name}
                                  className="w-6 h-6 rounded-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none'
                                  }}
                                />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-purple-200 flex items-center justify-center">
                                  <i className="fas fa-user text-purple-600 text-xs"></i>
                                </div>
                              )}
                              <span className="text-xs font-medium text-purple-700">
                                {associatedProfessional.name}
                              </span>
                            </div>
                            {proDeactivated && <DeactivatedTag />}
                          </div>
                        )}
                      </div>
                    </div>
                    {service.description && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">{service.description}</p>
                    )}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-500"><i className="far fa-clock mr-1.5"></i>{getServiceDuration()}</span>
                        <span className="text-primary font-bold text-base">{formatPriceFrom(getServicePrice(), currency)}</span>
                      </div>
                      <button
                        disabled={bookingBlocked}
                        onClick={() => {
                          if (bookingBlocked) return
                          // Close sidebar first
                          if (onCloseSidebar) {
                            onCloseSidebar()
                          }
                          
                          // Prepare service data with associated professional
                          const serviceData = {
                            ...service,
                            // Ensure we have the required fields
                            duration: getServiceDuration(),
                            // IMPORTANT: keep booking data numeric, let modal format it for display
                            price: getServicePrice(),
                            price_from: getServicePrice(),
                            duration_minutes: parseDurationMinutes(service.duration_minutes ?? getServiceDuration(), 60),
                            group: serviceCategory?.name || service.category_name || 'Featured',
                            category_id: serviceCategory?.id || service.category_id || null,
                            category_name: serviceCategory?.name || service.category_name || null,
                            // Add associated professional if available
                            professional: associatedProfessional,
                            professional_id: associatedProfessional?.id || null
                          }
                          
                          // If we have both service and professional, skip to date/time step
                          if (associatedProfessional && setBookingData && setShowServiceBooking && setServiceBookingStep) {
                            setTimeout(() => {
                              // Set booking data with service and professional pre-selected
                              setBookingData({
                                services: [serviceData],
                                specialist: associatedProfessional,
                                date: '',
                                time: null,
                                paymentMethod: 'card',
                                discountCode: '',
                                discount: 0
                              })
                              // Open booking modal at step 2 (date/time selection)
                              setServiceBookingStep(2)
                              setShowServiceBooking(true)
                            }, 150)
                          } else {
                            // Small delay to allow sidebar close animation
                            setTimeout(() => {
                          // Navigate to home page with service data
                          if (navigate) {
                            navigate('/', { 
                              state: { 
                                bookService: serviceData,
                                scrollTo: 'services'
                              } 
                            })
                          } else if (onBookNow) {
                            // Fallback to onBookNow if navigate not available
                            onBookNow()
                              }
                            }, 150)
                          }
                        }}
                        className={`px-4 py-2 rounded-lg font-medium text-sm shadow-md transition-all duration-200 ${
                          bookingBlocked
                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 hover:shadow-lg transform hover:scale-105 active:scale-95'
                        }`}
                      >
                        {bookingBlocked ? 'Unavailable' : 'Book Now'}
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )
      ) : favoriteTab === 'staff' ? (
        displayFavoriteSpecialists.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 mb-4">
              <i className="far fa-heart text-3xl text-purple-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No favorite professionals yet</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">Tap the heart icon on any professional to save them here for quick access.</p>
        </div>
      ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {displayFavoriteSpecialists.map((specialist) => {
            // Get associated services if available
            const associatedServices = specialist.associatedServices || []
            const proDeactivated =
              specialist.isDeactivated === true || specialist.is_active === false
            const hasDeactivatedService = associatedServices.some(
              (s) => s.isDeactivated === true || s.is_active === false
            )
            const bookingBlocked = proDeactivated || hasDeactivatedService
            
            return (
            <div
              key={specialist.id}
              className="group bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg hover:border-purple-200 transition-all duration-200"
            >
              <div className="relative h-40 overflow-hidden">
                <img
                  src={optimizeProfessionalImage(specialist.image_url || specialist.image, 'medium')}
                  alt={specialist.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                  width="400"
                  height="160"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x300?text=No+Image'
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                {proDeactivated && (
                  <div className="absolute top-3 left-3">
                    <DeactivatedTag />
                  </div>
                )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleFavorite('specialists', specialist.id)
                    }}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm text-red-500 flex items-center justify-center hover:bg-white hover:scale-110 active:scale-95 transition-all duration-200 shadow-md"
                    aria-label="Remove favorite specialist"
                  >
                  <i className="fas fa-heart text-xs"></i>
                  </button>
              </div>
              <div className="p-4">
                <div className="mb-3">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-bold text-lg text-gray-900">
                      {specialist.name}
                    </h3>
                    {proDeactivated && <DeactivatedTag />}
                  </div>
                  <p className="text-sm text-purple-600 font-medium mb-2">
                    {specialist.specialty || specialist.role}
                  </p>
                  {/* Show associated services if available */}
                  {associatedServices && associatedServices.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 mb-1.5">Selected Services:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {associatedServices.slice(0, 3).map((service, idx) => (
                          <span
                            key={service.id || idx}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 text-xs font-medium border border-purple-200"
                          >
                            {service.name || 'Service'}
                            {(service.isDeactivated === true || service.is_active === false) && (
                              <span className="text-[9px] uppercase font-bold text-gray-500">· Off</span>
                            )}
                          </span>
                        ))}
                        {associatedServices.length > 3 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-purple-100 text-purple-700 text-xs font-medium border border-purple-200">
                            +{associatedServices.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center mb-4">
                  <div className="flex text-yellow-400 text-xs">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={
                          i < Math.floor(specialist.rating || 0)
                            ? 'fas fa-star'
                            : 'far fa-star'
                        }
                      ></i>
                    ))}
                  </div>
                  <span className="ml-2 text-xs text-gray-500">
                    {Number(specialist.rating || 0).toFixed(1)}
                    {(specialist.reviewCount > 0 || specialist.reviews > 0)
                      ? ` (${specialist.reviewCount || specialist.reviews} reviews)`
                      : ''}
                  </span>
                </div>
                <button
                  disabled={bookingBlocked}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (bookingBlocked) return
                    
                    // Close sidebar first
                    if (onCloseSidebar) {
                      onCloseSidebar()
                    }
                    
                    // If we have associated services, skip to date/time step
                    if (associatedServices.length > 0 && setBookingData && setShowServiceBooking && setServiceBookingStep) {
                      setTimeout(() => {
                        // Prepare services with professional
                        const servicesWithProfessional = associatedServices.map(service => ({
                          ...service,
                          professional: specialist,
                          professional_id: specialist.id,
                          duration: typeof service.duration === 'string'
                            ? service.duration
                            : `${parseDurationMinutes(service.duration_minutes ?? service.duration, 60)} min`,
                          duration_minutes: parseDurationMinutes(service.duration_minutes ?? service.duration, 60),
                          price: parsePriceNumber(service.price ?? service.price_from ?? 0, 0),
                          price_from: parsePriceNumber(service.price_from ?? service.price ?? 0, 0)
                        }))
                        
                        // Set booking data with services and professional pre-selected
                        setBookingData({
                          services: servicesWithProfessional,
                          specialist: specialist,
                          date: '',
                          time: null,
                          paymentMethod: 'card',
                          discountCode: '',
                          discount: 0
                        })
                        // Open booking modal at step 2 (date/time selection)
                        setServiceBookingStep(2)
                        setShowServiceBooking(true)
                      }, 150)
                    } else {
                    // Prepare specialist data similar to FreshaApp
                    const specialistData = {
                      ...specialist,
                      // Normalize image field
                      image: specialist.image || specialist.image_url || null,
                      image_url: specialist.image_url || specialist.image || null,
                      specializations: specialist.specializations || [specialist.role] || [specialist.specialty] || ['Featured']
                    }
                    
                      // Small delay to allow sidebar close animation
                      setTimeout(() => {
                    // Navigate to home page with specialist data
                    if (navigate) {
                      navigate('/', { 
                        state: { 
                          bookSpecialist: specialistData.id,
                          specialist: specialistData,
                          scrollTo: 'services'
                        } 
                      })
                    } else if (onBookNow) {
                      // Fallback to onBookNow if navigate not available
                      onBookNow()
                        }
                      }, 150)
                    }
                  }}
                  className={`w-full py-2.5 px-4 rounded-lg font-medium text-sm shadow-md transition-all duration-200 ${
                    bookingBlocked
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 hover:shadow-lg transform hover:scale-105 active:scale-95'
                  }`}
                >
                  {bookingBlocked ? 'Unavailable' : 'Book Now'}
                </button>
              </div>
            </div>
            )
          })}
          </div>
        )
      ) : favoriteTab === 'appointments' ? (
        (favoriteAppointments && Array.isArray(favoriteAppointments) && favoriteAppointments.length > 0) ? (
          <div className="grid grid-cols-1 gap-4">
            {favoriteAppointments.map((favAppointment) => {
              // Handle both direct appointment_data and nested structure
              const appointmentData = favAppointment.appointment_data || favAppointment || {}
              const appointmentServices = appointmentData.services || []
              const appointmentSpecialist = appointmentData.specialist || appointmentData.professional || null
              const appointmentProfessionalDisplay = getBookingProfessionalDisplay({
                ...appointmentData,
                services: appointmentServices,
                professionalData:
                  appointmentData.professionalData ||
                  appointmentData.professional_data ||
                  appointmentData.professional_data_json ||
                  null,
                specialist: appointmentSpecialist
              })
              const proDeactivated =
                appointmentSpecialist?.isDeactivated === true ||
                appointmentSpecialist?.is_active === false ||
                favAppointment.hasDeactivatedProfessional === true
              const hasDeactivatedService =
                favAppointment.hasDeactivatedService === true ||
                appointmentServices.some(
                  (s) => s.isDeactivated === true || s.is_active === false
                )
              const bookingBlocked =
                favAppointment.isDeactivated === true ||
                proDeactivated ||
                hasDeactivatedService
              
              return (
                <div key={favAppointment.id} className="group bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-lg hover:border-purple-200 transition-all duration-200 overflow-hidden">
                  <div className="p-5">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 pr-3">
                        <h3 className="font-bold text-lg text-gray-900 mb-2">Favorite Appointment</h3>
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <p className="text-sm text-purple-600 font-medium">
                              {appointmentProfessionalDisplay || appointmentSpecialist?.name || 'Any Specialist'}
                            </p>
                            {proDeactivated && <DeactivatedTag />}
                          </div>
                      </div>
                      <button
                        onClick={async () => {
                          if (!onRemoveFavoriteAppointment) return
                          try {
                            await onRemoveFavoriteAppointment(favAppointment.id)
                          } catch (error) {
                            console.error('Error removing favorite appointment:', error)
                          }
                        }}
                        className="w-7 h-7 rounded-full bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 active:scale-95 transition-all duration-200 opacity-0 group-hover:opacity-100"
                        aria-label="Remove favorite appointment"
                      >
                        <i className="fas fa-heart text-xs"></i>
                      </button>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <p className="text-sm font-semibold text-gray-700">Services:</p>
                      {appointmentServices.map((service, idx) => (
                        <div key={idx} className="text-sm text-gray-600 flex items-center justify-between gap-2">
                          <span className="flex items-center gap-2 flex-wrap min-w-0">
                            <span>• {service.name || 'Service'}</span>
                            {(service.isDeactivated === true || service.is_active === false) && (
                              <DeactivatedTag />
                            )}
                          </span>
                          {service.price && (
                            <span className="text-primary font-semibold shrink-0">
                              {formatPrice(parseFloat(service.price || 0), selectedCurrency)}
                            </span>
                          )}
            </div>
          ))}
        </div>
                    
                    <button
                      disabled={bookingBlocked}
                      onClick={() => {
                        if (bookingBlocked) return
                        if (onCloseSidebar) {
                          onCloseSidebar()
                        }
                        
                        setTimeout(() => {
                          if (setBookingData && setShowServiceBooking && setServiceBookingStep) {
                            const normalizedAppointmentServices = (appointmentServices || []).map((s) => ({
                              ...s,
                              duration: typeof s.duration === 'string'
                                ? s.duration
                                : (s.duration_minutes
                                    ? `${parseDurationMinutes(s.duration_minutes, 60)} min`
                                    : (typeof s.duration === 'number'
                                        ? `${parseDurationMinutes(s.duration, 60)} min`
                                        : '60 min')),
                              duration_minutes: parseDurationMinutes(s.duration_minutes ?? s.duration, 60),
                              price: parsePriceNumber(s.price ?? s.price_from ?? 0, 0),
                              price_from: parsePriceNumber(s.price_from ?? s.price ?? 0, 0)
                            }))
                            // Pre-fill booking data with favorited appointment
                            setBookingData({
                              services: normalizedAppointmentServices,
                              specialist: appointmentSpecialist,
                              date: '',
                              time: null,
                              paymentMethod: 'card',
                              discountCode: '',
                              discount: 0
                            })
                            // Open booking modal at step 2 (date/time selection)
                            setServiceBookingStep(2)
                            setShowServiceBooking(true)
                          }
                        }, 150)
                      }}
                      className={`w-full px-4 py-2 rounded-lg font-medium text-sm shadow-md transition-all duration-200 ${
                        bookingBlocked
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 hover:shadow-lg transform hover:scale-105 active:scale-95'
                      }`}
                    >
                      {bookingBlocked ? 'Unavailable' : 'Book Now'}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-16 px-4">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 mb-4">
              <i className="far fa-calendar-check text-3xl text-purple-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No favorite appointments yet</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">Favorite appointments from the review page to save them here for quick booking.</p>
          </div>
        )
      ) : null}
    </div>
  )
}

export default FavoritesTab
